import {
    CompanyValue,
    CompanyValueList,
    Occasion,
    OccasionList,
    Recognition,
    RecognitionInput,
    Team,
    TeamList,
    TeamState,
    User,
    UserList
} from "../../types"
import client from "../../utils/clients/apollo"
import {
    COMPANY_VALUES_QUERY,
    CREATE_RECOGNITION_MUTATION,
    OCCASIONS_QUERY,
    TEAMS_QUERY,
    USERS_FOR_BUSINESS_QUERY
} from "./queries"

interface CompanyValuesResponse {
    companyValues: CompanyValueList
}

interface OccasionsResponse {
    occasions: OccasionList
}

interface UsersResponse {
    usersForBusiness: UserList
}

interface CreateRecognitionResponse {
    createRecognition: Recognition
}

interface TeamsResponse {
    teams: TeamList
}

class RecognitionService {
    async getCompanyValues(businessId: string): Promise<CompanyValue[]> {
        const res = await client.query<CompanyValuesResponse>({
            query: COMPANY_VALUES_QUERY,
            variables: {
                limit: 9999,
                tenantId: businessId
            }
        })

        return res.data.companyValues.results
    }

    async getOccasions(businessId: string): Promise<Occasion[]> {
        const res = await client.query<OccasionsResponse>({
            query: OCCASIONS_QUERY,
            variables: {
                tenantId: businessId
            }
        })

        return res.data.occasions.results.filter(o => {return !o.archived})
    }

    async getUsers(businessId: string, searchString: string, cursor?: string): Promise<User[]> {
        if (!searchString) {
            return []
        }

        const users: User[] = []
        const res = await client.query<UsersResponse>({
            query: USERS_FOR_BUSINESS_QUERY,
            variables: {
                businessId: businessId,
                enabled: true,
                includeBusiness: false,
                searchString,
                cursor
            }
        })

        users.push(...res.data.usersForBusiness.results)

        if (res.data.usersForBusiness.cursor) {
            users.push(...await this.getUsers(businessId, searchString, res.data.usersForBusiness.cursor))
        }

        users.sort((a, b): number => {
            const aFullName = `${a.firstName} ${a.lastName}`
            const bFullName = `${b.firstName} ${b.lastName}`

            if (aFullName < bFullName) {
                return -1
            } else if (aFullName > bFullName) {
                return 1
            }

            return 0
        })

        return users
    }

    async getTeams(businessId: string): Promise<Team[]> {
        const res = await client.query<TeamsResponse>({
            query: TEAMS_QUERY,
            variables: {
                tenantId: businessId,
                enabled: true,
                state: TeamState.Generic
            }
        })

        return res.data.teams.results
    }

    async createRecognition(recognition: Partial<RecognitionInput>): Promise<any> {
        const res = await client.mutate<CreateRecognitionResponse>({
            mutation: CREATE_RECOGNITION_MUTATION,
            variables: {
                recognition
            }
        })

        return res.data?.createRecognition
    }
}

const recognitionService = new RecognitionService()

export default recognitionService
